import React from 'react';
import { Config } from '@backstage/config';
import { Entity } from '@backstage/catalog-model';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  isKubernetesAvailable,
  kubernetesApiRef,
  type KubernetesApi,
} from '@backstage/plugin-kubernetes';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { EmptyState, InfoCard, Progress } from '@backstage/core-components';
import {
  Box,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

type CrossplaneMatcher = {
  group: string;
  apiVersion: string;
  plural: string;
};

type CrossplaneCondition = {
  type?: string;
  status?: string;
  reason?: string;
};

type CrossplaneOwnerReference = {
  uid?: string;
  kind?: string;
  name?: string;
  apiVersion?: string;
};

type CrossplaneResourceRef = {
  apiVersion?: string;
  kind?: string;
  name?: string;
  namespace?: string;
};

type CrossplaneResource = {
  apiVersion?: string;
  kind?: string;
  metadata?: {
    uid?: string;
    name?: string;
    namespace?: string;
    labels?: Record<string, string>;
    ownerReferences?: CrossplaneOwnerReference[];
  };
  spec?: {
    resourceRefs?: CrossplaneResourceRef[];
  };
  status?: {
    phase?: string;
    state?: string;
    conditions?: CrossplaneCondition[];
  };
};

type CrossplaneNode = {
  id: string;
  resource: CrossplaneResource;
  synced: string;
  ready: string;
  status: string;
  children: CrossplaneNode[];
};

type CrossplaneClusterResources = {
  clusterName: string;
  resources: CrossplaneResource[];
};

type CrossplaneClusterTarget = {
  name: string;
};

type CrossplaneClusterTrace = {
  clusterName: string;
  roots: CrossplaneNode[];
  totalResources: number;
  fetchedResources: number;
};

type ClusterWideFetchResponse = {
  results: CrossplaneClusterResources[];
  warnings: string[];
};

type EntitySelectors = {
  explicitNames: Set<string>;
  namePattern?: RegExp;
  kubernetesId?: string;
  entityName: string;
};

const REFRESH_INTERVAL_MS = 60_000;
const ANNOTATION_RESOURCE_NAME = 'physicsx.ai/crossplane-resource-name';
const ANNOTATION_RESOURCE_NAMES = 'physicsx.ai/crossplane-resource-names';
const ANNOTATION_RESOURCE_NAME_PATTERN =
  'physicsx.ai/crossplane-resource-name-pattern';
const ANNOTATION_KUBERNETES_ID = 'backstage.io/kubernetes-id';
const ANNOTATION_CROSSPLANE_CLUSTER = 'physicsx.ai/crossplane-cluster';
const ANNOTATION_CROSSPLANE_CLUSTERS = 'physicsx.ai/crossplane-clusters';
const ANNOTATION_KUBERNETES_CLUSTER = 'backstage.io/kubernetes-cluster';

const DEFAULT_MATCHERS: CrossplaneMatcher[] = [
  {
    group: 'cloud.physicsx.ai',
    apiVersion: 'v1beta1',
    plural: 'databases',
  },
  {
    group: 'cloud.physicsx.ai',
    apiVersion: 'v1beta1',
    plural: 'databaseinstances',
  },
  {
    group: 'cloud.physicsx.ai',
    apiVersion: 'v1beta1',
    plural: 'caches',
  },
  {
    group: 'cloud.physicsx.ai',
    apiVersion: 'v1beta1',
    plural: 'buckets',
  },
  {
    group: 'cloud.physicsx.ai',
    apiVersion: 'v1beta1',
    plural: 's3buckets',
  },
];

const normalize = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }
  return value.trim().toLowerCase();
};

const isKubernetesClusterEntity = (entity: Entity): boolean => {
  if (normalize(entity.kind) !== 'resource') {
    return false;
  }
  const entityType = normalize(
    (entity.spec as { type?: string } | undefined)?.type,
  );
  return entityType === 'kubernetes-cluster';
};

const conditionValueStyle = (value: string): React.CSSProperties | undefined => {
  if (value === 'True') {
    return { color: '#2e7d32', fontWeight: 600 };
  }
  if (value === 'False') {
    return { color: '#d32f2f', fontWeight: 600 };
  }
  return undefined;
};

const resourceRefKey = (resource: {
  apiVersion?: string;
  kind?: string;
  namespace?: string;
  name?: string;
}): string =>
  [
    resource.apiVersion || '',
    resource.kind || '',
    resource.namespace || '',
    resource.name || '',
  ].join('|');

const nodeLabel = (resource: CrossplaneResource): string => {
  const kind = resource.kind || 'Resource';
  const name = resource.metadata?.name || 'unknown';
  return `${kind}/${name}`;
};

const nodeSort = (a: CrossplaneNode, b: CrossplaneNode): number =>
  nodeLabel(a.resource).localeCompare(nodeLabel(b.resource));

const getCondition = (
  resource: CrossplaneResource,
  conditionType: string,
): CrossplaneCondition | undefined => {
  const conditions = resource.status?.conditions;
  if (!Array.isArray(conditions)) {
    return undefined;
  }
  return conditions.find(c => c?.type === conditionType);
};

const getNodeStatus = (
  resource: CrossplaneResource,
): Pick<CrossplaneNode, 'synced' | 'ready' | 'status'> => {
  const syncedCondition = getCondition(resource, 'Synced');
  const readyCondition = getCondition(resource, 'Ready');

  return {
    synced: syncedCondition?.status || '-',
    ready: readyCondition?.status || '-',
    status:
      readyCondition?.reason ||
      syncedCondition?.reason ||
      resource.status?.phase ||
      resource.status?.state ||
      '-',
  };
};

const parseMatchersFromConfig = (configApi: Config): CrossplaneMatcher[] => {
  const configuredMatchers = configApi.getOptionalConfigArray(
    'crossplane.customResources',
  );

  const parsedMatchers = (configuredMatchers || []).reduce<CrossplaneMatcher[]>(
    (acc, matcherConfig) => {
      try {
        const group = matcherConfig.getString('group');
        const apiVersion = matcherConfig.getString('apiVersion');
        const plural = matcherConfig.getString('plural');
        if (group && apiVersion && plural) {
          acc.push({ group, apiVersion, plural });
        }
      } catch {
        // Ignore invalid matcher entries and keep parsing.
      }
      return acc;
    },
    [],
  );

  // Always include the database baseline matchers so referenced subresources
  // can be resolved even if config is partially specified.
  const byKey = new Map<string, CrossplaneMatcher>();
  for (const matcher of [...DEFAULT_MATCHERS, ...parsedMatchers]) {
    byKey.set(`${matcher.group}|${matcher.apiVersion}|${matcher.plural}`, matcher);
  }
  return [...byKey.values()];
};

const parseCsv = (value: string | undefined): string[] =>
  (value || '')
    .split(',')
    .map(v => normalize(v))
    .filter((v): v is string => Boolean(v));

const parseCsvRaw = (value: string | undefined): string[] =>
  (value || '')
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);

const parseEntitySelectors = (entity: Entity): EntitySelectors => {
  const annotations = entity.metadata.annotations || {};

  const explicitNames = new Set<string>();
  parseCsv(annotations[ANNOTATION_RESOURCE_NAMES]).forEach(name =>
    explicitNames.add(name),
  );

  const singleName = normalize(annotations[ANNOTATION_RESOURCE_NAME]);
  if (singleName) {
    explicitNames.add(singleName);
  }

  const kubernetesId = normalize(annotations[ANNOTATION_KUBERNETES_ID]);
  if (kubernetesId) {
    explicitNames.add(kubernetesId);
  }

  const patternSource = annotations[ANNOTATION_RESOURCE_NAME_PATTERN];
  let namePattern: RegExp | undefined;
  if (patternSource) {
    try {
      namePattern = new RegExp(patternSource, 'i');
    } catch {
      // Ignore invalid regex patterns in annotations.
    }
  }

  return {
    explicitNames,
    namePattern,
    kubernetesId,
    entityName: normalize(entity.metadata.name) || '',
  };
};

const parseClusterScopeFromEntity = (entity: Entity): string[] => {
  const annotations = entity.metadata.annotations || {};
  const unique = new Set<string>();
  for (const clusterName of parseCsvRaw(annotations[ANNOTATION_CROSSPLANE_CLUSTER])) {
    unique.add(clusterName);
  }
  for (const clusterName of parseCsvRaw(annotations[ANNOTATION_CROSSPLANE_CLUSTERS])) {
    unique.add(clusterName);
  }
  for (const clusterName of parseCsvRaw(annotations[ANNOTATION_KUBERNETES_CLUSTER])) {
    unique.add(clusterName);
  }
  if (isKubernetesClusterEntity(entity) && entity.metadata.name) {
    unique.add(entity.metadata.name);
  }
  return [...unique];
};

const isSeedResource = (
  resource: CrossplaneResource,
  selectors: EntitySelectors,
): boolean => {
  const name = normalize(resource.metadata?.name);
  if (!name) {
    return false;
  }

  if (selectors.explicitNames.size > 0 && selectors.explicitNames.has(name)) {
    return true;
  }

  if (selectors.namePattern && selectors.namePattern.test(name)) {
    return true;
  }

  const labels = resource.metadata?.labels || {};
  const claimName = normalize(labels['crossplane.io/claim-name']);
  const compositeName = normalize(labels['crossplane.io/composite']);
  const resourceKubernetesId = normalize(labels[ANNOTATION_KUBERNETES_ID]);
  const appNameLabel = normalize(labels['app.kubernetes.io/name']);
  const appInstanceLabel = normalize(labels['app.kubernetes.io/instance']);

  if (
    selectors.kubernetesId &&
    (claimName === selectors.kubernetesId ||
      compositeName === selectors.kubernetesId ||
      resourceKubernetesId === selectors.kubernetesId ||
      name.includes(selectors.kubernetesId))
  ) {
    return true;
  }

  if (claimName === selectors.entityName || compositeName === selectors.entityName) {
    return true;
  }

  if (name.includes(selectors.entityName)) {
    return true;
  }

  if (
    (appNameLabel && appNameLabel.includes(selectors.entityName)) ||
    (appInstanceLabel && appInstanceLabel.includes(selectors.entityName))
  ) {
    return true;
  }

  return false;
};

const findAssociatedResources = (
  resources: CrossplaneResource[],
  entity: Entity,
): CrossplaneResource[] => {
  if (!resources.length) {
    return [];
  }

  const selectors = parseEntitySelectors(entity);

  const resourceKeys: string[] = [];
  const resourceByKey = new Map<string, CrossplaneResource>();
  const keyByUid = new Map<string, string>();
  const keyByRef = new Map<string, string>();

  resources.forEach((resource, index) => {
    const metadata = resource.metadata;
    const key =
      metadata?.uid ||
      `${resourceRefKey({
        apiVersion: resource.apiVersion,
        kind: resource.kind,
        namespace: metadata?.namespace,
        name: metadata?.name,
      })}|${index}`;

    resourceKeys.push(key);
    resourceByKey.set(key, resource);

    if (metadata?.uid) {
      keyByUid.set(metadata.uid, key);
    }

    keyByRef.set(
      resourceRefKey({
        apiVersion: resource.apiVersion,
        kind: resource.kind,
        namespace: metadata?.namespace,
        name: metadata?.name,
      }),
      key,
    );
    keyByRef.set(
      resourceRefKey({
        apiVersion: resource.apiVersion,
        kind: resource.kind,
        name: metadata?.name,
      }),
      key,
    );
  });

  const seedKeys = resources
    .map((resource, index) => ({ resource, key: resourceKeys[index] }))
    .filter(entry => isSeedResource(entry.resource, selectors))
    .map(entry => entry.key);

  if (!seedKeys.length) {
    return [];
  }

  const neighbors = new Map<string, Set<string>>();
  resourceKeys.forEach(key => neighbors.set(key, new Set<string>()));

  resources.forEach((resource, index) => {
    const childKey = resourceKeys[index];
    const childNamespace = resource.metadata?.namespace;

    for (const owner of resource.metadata?.ownerReferences || []) {
      let parentKey: string | undefined;

      if (owner.uid) {
        parentKey = keyByUid.get(owner.uid);
      }

      if (!parentKey && owner.kind && owner.name) {
        parentKey =
          keyByRef.get(
            resourceRefKey({
              apiVersion: owner.apiVersion,
              kind: owner.kind,
              namespace: childNamespace,
              name: owner.name,
            }),
          ) ||
          keyByRef.get(
            resourceRefKey({
              apiVersion: owner.apiVersion,
              kind: owner.kind,
              name: owner.name,
            }),
          );
      }

      if (parentKey && parentKey !== childKey) {
        neighbors.get(childKey)?.add(parentKey);
        neighbors.get(parentKey)?.add(childKey);
      }
    }

    // Connect resources through explicit Crossplane resourceRefs as well.
    // This makes fetched subresources part of the associated graph, so their
    // real Synced/Ready conditions are shown instead of "Referenced".
    for (const ref of resource.spec?.resourceRefs || []) {
      const refNamespace = ref.namespace || childNamespace;
      const referencedKey =
        keyByRef.get(
          resourceRefKey({
            apiVersion: ref.apiVersion,
            kind: ref.kind,
            namespace: refNamespace,
            name: ref.name,
          }),
        ) ||
        keyByRef.get(
          resourceRefKey({
            apiVersion: ref.apiVersion,
            kind: ref.kind,
            name: ref.name,
          }),
        );

      if (referencedKey && referencedKey !== childKey) {
        neighbors.get(childKey)?.add(referencedKey);
        neighbors.get(referencedKey)?.add(childKey);
      }
    }
  });

  const queue = [...seedKeys];
  const visited = new Set<string>(seedKeys);

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) {
      continue;
    }
    for (const next of neighbors.get(current) || []) {
      if (!visited.has(next)) {
        visited.add(next);
        queue.push(next);
      }
    }
  }

  return resourceKeys
    .filter(key => visited.has(key))
    .map(key => resourceByKey.get(key))
    .filter((resource): resource is CrossplaneResource => Boolean(resource));
};

const buildTraceTree = (
  resources: CrossplaneResource[],
  clusterName: string,
): CrossplaneNode[] => {
  const nodesById = new Map<string, CrossplaneNode>();
  const nodesByRef = new Map<string, CrossplaneNode>();
  const nodes: CrossplaneNode[] = [];
  const attachedChildren = new Map<string, Set<string>>();

  resources.forEach(resource => {
    const metadata = resource.metadata;
    const fallbackId = `${clusterName}:${resource.kind || 'Resource'}:${metadata?.namespace || ''}:${metadata?.name || 'unknown'}`;
    const nodeId = metadata?.uid || fallbackId;

    const node: CrossplaneNode = {
      id: nodeId,
      resource,
      ...getNodeStatus(resource),
      children: [],
    };

    nodesById.set(nodeId, node);
    nodes.push(node);
    nodesByRef.set(
      resourceRefKey({
        apiVersion: resource.apiVersion,
        kind: resource.kind,
        namespace: metadata?.namespace,
        name: metadata?.name,
      }),
      node,
    );
    nodesByRef.set(
      resourceRefKey({
        apiVersion: resource.apiVersion,
        kind: resource.kind,
        name: metadata?.name,
      }),
      node,
    );
  });

  const hasParent = new Set<string>();
  const attachChild = (parentNode: CrossplaneNode, childNode: CrossplaneNode) => {
    if (parentNode.id === childNode.id) {
      return;
    }
    const parentChildren = attachedChildren.get(parentNode.id) || new Set<string>();
    if (parentChildren.has(childNode.id)) {
      return;
    }
    parentChildren.add(childNode.id);
    attachedChildren.set(parentNode.id, parentChildren);
    parentNode.children.push(childNode);
    hasParent.add(childNode.id);
  };

  for (const node of nodes) {
    const childNamespace = node.resource.metadata?.namespace;
    for (const owner of node.resource.metadata?.ownerReferences || []) {
      let parentNode: CrossplaneNode | undefined;

      if (owner.uid) {
        parentNode = nodesById.get(owner.uid);
      }

      if (!parentNode && owner.kind && owner.name) {
        parentNode =
          nodesByRef.get(
            resourceRefKey({
              apiVersion: owner.apiVersion,
              kind: owner.kind,
              namespace: childNamespace,
              name: owner.name,
            }),
          ) ||
          nodesByRef.get(
            resourceRefKey({
              apiVersion: owner.apiVersion,
              kind: owner.kind,
              name: owner.name,
            }),
          );
      }

      if (parentNode) {
        attachChild(parentNode, node);
        break;
      }
    }
  }

  const getOrCreateRefNode = (
    ref: CrossplaneResourceRef,
    fallbackNamespace?: string,
  ): CrossplaneNode | undefined => {
    const kind = ref.kind;
    const name = ref.name;
    if (!kind || !name) {
      return undefined;
    }

    const namespace = ref.namespace || fallbackNamespace;
    const existingNode =
      nodesByRef.get(
        resourceRefKey({
          apiVersion: ref.apiVersion,
          kind,
          namespace,
          name,
        }),
      ) ||
      nodesByRef.get(
        resourceRefKey({
          apiVersion: ref.apiVersion,
          kind,
          name,
        }),
      );

    if (existingNode) {
      return existingNode;
    }

    const syntheticId = `ref:${clusterName}:${ref.apiVersion || ''}:${kind}:${namespace || ''}:${name}`;
    const existingSynthetic = nodesById.get(syntheticId);
    if (existingSynthetic) {
      return existingSynthetic;
    }

    const syntheticNode: CrossplaneNode = {
      id: syntheticId,
      resource: {
        apiVersion: ref.apiVersion,
        kind,
        metadata: {
          name,
          namespace,
        },
      },
      synced: '-',
      ready: '-',
      status: 'Referenced',
      children: [],
    };

    nodesById.set(syntheticId, syntheticNode);
    nodes.push(syntheticNode);
    nodesByRef.set(
      resourceRefKey({
        apiVersion: ref.apiVersion,
        kind,
        namespace,
        name,
      }),
      syntheticNode,
    );
    nodesByRef.set(
      resourceRefKey({
        apiVersion: ref.apiVersion,
        kind,
        name,
      }),
      syntheticNode,
    );

    return syntheticNode;
  };

  for (const parentNode of nodes) {
    const fallbackNamespace = parentNode.resource.metadata?.namespace;
    for (const ref of parentNode.resource.spec?.resourceRefs || []) {
      const childNode = getOrCreateRefNode(ref, fallbackNamespace);
      if (childNode) {
        attachChild(parentNode, childNode);
      }
    }
  }

  nodes.forEach(node => node.children.sort(nodeSort));

  const roots = nodes.filter(node => !hasParent.has(node.id)).sort(nodeSort);
  const primaryRoots = roots.filter(
    node =>
      node.resource.kind === 'Database' ||
      node.resource.kind === 'Cache' ||
      node.resource.kind === 'Bucket' ||
      node.resource.kind === 'S3Bucket',
  );
  return primaryRoots.length ? primaryRoots : roots;
};

const buildClusterTraces = (
  clusters: CrossplaneClusterResources[],
  entity: Entity,
  showAll: boolean,
): CrossplaneClusterTrace[] => {
  const traces: CrossplaneClusterTrace[] = [];

  for (const cluster of clusters) {
    const visibleResources = showAll
      ? cluster.resources
      : findAssociatedResources(cluster.resources, entity);

    if (!visibleResources.length) {
      continue;
    }

    traces.push({
      clusterName: cluster.clusterName,
      roots: buildTraceTree(visibleResources, cluster.clusterName),
      totalResources: visibleResources.length,
      fetchedResources: cluster.resources.length,
    });
  }

  return traces;
};

const buildClusterWidePath = (matcher: CrossplaneMatcher): string =>
  `/apis/${matcher.group}/${matcher.apiVersion}/${matcher.plural}`;

const DATABASE_ROOT_PLURALS = new Set([
  'databases',
  'databaseinstances',
  'caches',
  'buckets',
  's3buckets',
]);

const isDatabaseRootMatcher = (matcher: CrossplaneMatcher): boolean =>
  matcher.group === 'cloud.physicsx.ai' &&
  DATABASE_ROOT_PLURALS.has(normalize(matcher.plural) || '');

const resolveTargetClusters = async (
  kubernetesApi: KubernetesApi,
  scopedClusterNames: string[],
): Promise<CrossplaneClusterTarget[]> =>
  scopedClusterNames.length > 0
    ? scopedClusterNames.map(name => ({ name }))
    : (await kubernetesApi.getClusters()).map(cluster => ({ name: cluster.name }));

const fetchMatcherResources = async (
  kubernetesApi: KubernetesApi,
  clusterName: string,
  matcher: CrossplaneMatcher,
): Promise<{ resources: CrossplaneResource[]; warnings: string[] }> => {
  const warnings: string[] = [];
  const path = buildClusterWidePath(matcher);
  let response: Response;
  try {
    response = await kubernetesApi.proxy({
      clusterName,
      path,
      init: { method: 'GET' },
    });
  } catch (error) {
    warnings.push(`Cluster "${clusterName}" ${path} request failed: ${String(error)}`);
    return { resources: [], warnings };
  }

  if (response.status === 404) {
    // CRD not installed on this cluster, expected for some clusters.
    return { resources: [], warnings };
  }

  if (!response.ok) {
    const body = await response.text();
    warnings.push(`Cluster "${clusterName}" ${path} returned ${response.status}: ${body}`);
    return { resources: [], warnings };
  }

  try {
    const payload = (await response.json()) as {
      items?: unknown[];
      rows?: Array<{ object?: unknown }>;
    };
    if (Array.isArray(payload.items)) {
      return { resources: payload.items as CrossplaneResource[], warnings };
    }
    if (Array.isArray(payload.rows)) {
      const rowObjects = payload.rows
        .map(row => row?.object)
        .filter((obj): obj is CrossplaneResource => Boolean(obj));
      if (rowObjects.length > 0) {
        return { resources: rowObjects, warnings };
      }
    }
    warnings.push(
      `Cluster "${clusterName}" ${path} returned 200 but no list items were present`,
    );
  } catch (error) {
    warnings.push(
      `Cluster "${clusterName}" ${path} returned invalid JSON: ${String(error)}`,
    );
  }

  return { resources: [], warnings };
};

const fetchClusterWideResourcesForMatchers = async (
  kubernetesApi: KubernetesApi,
  clusters: CrossplaneClusterTarget[],
  matchers: CrossplaneMatcher[],
): Promise<ClusterWideFetchResponse> => {
  const warnings: string[] = [];

  if (clusters.length === 0 || matchers.length === 0) {
    return { results: [], warnings };
  }

  const results = await Promise.all(
    clusters.map(async cluster => {
      const resources: CrossplaneResource[] = [];

      for (const matcher of matchers) {
        const matcherResponse = await fetchMatcherResources(
          kubernetesApi,
          cluster.name,
          matcher,
        );
        resources.push(...matcherResponse.resources);
        warnings.push(...matcherResponse.warnings);
      }

      return {
        clusterName: cluster.name,
        resources,
      };
    }),
  );

  return {
    results: results.filter(result => result.resources.length > 0),
    warnings,
  };
};

const mergeWarnings = (existing: string[], incoming: string[]): string[] => {
  if (incoming.length === 0) {
    return existing;
  }
  return [...new Set([...existing, ...incoming])];
};

const resourceIdentity = (resource: CrossplaneResource): string =>
  resource.metadata?.uid ||
  resourceRefKey({
    apiVersion: resource.apiVersion,
    kind: resource.kind,
    namespace: resource.metadata?.namespace,
    name: resource.metadata?.name,
  });

const mergeClusterResources = (
  existing: CrossplaneClusterResources[],
  clusterName: string,
  incomingResources: CrossplaneResource[],
): CrossplaneClusterResources[] => {
  if (incomingResources.length === 0) {
    return existing;
  }

  const index = existing.findIndex(cluster => cluster.clusterName === clusterName);
  if (index === -1) {
    return [...existing, { clusterName, resources: incomingResources }];
  }

  const current = existing[index];
  const byKey = new Map<string, CrossplaneResource>();
  current.resources.forEach(resource => byKey.set(resourceIdentity(resource), resource));
  incomingResources.forEach(resource =>
    byKey.set(resourceIdentity(resource), resource),
  );

  const updated = [...existing];
  updated[index] = {
    clusterName,
    resources: [...byKey.values()],
  };
  return updated;
};

const useClusterWideCrossplaneResources = (
  matchers: CrossplaneMatcher[],
  refreshIntervalMs: number,
  scopedClusterNames: string[],
) => {
  const kubernetesApi = useApi(kubernetesApiRef);
  const [state, setState] = React.useState<{
    loading: boolean;
    backgroundLoading: boolean;
    error?: string;
    warnings: string[];
    clusters: CrossplaneClusterResources[];
  }>({
    loading: true,
    backgroundLoading: false,
    warnings: [],
    clusters: [],
  });

  const matchersKey = React.useMemo(() => JSON.stringify(matchers), [matchers]);
  const scopedClustersKey = React.useMemo(
    () => scopedClusterNames.join(','),
    [scopedClusterNames],
  );
  const rootMatchers = React.useMemo(
    () => matchers.filter(isDatabaseRootMatcher),
    [matchers],
  );
  const secondaryMatchers = React.useMemo(
    () =>
      rootMatchers.length > 0
        ? matchers.filter(matcher => !isDatabaseRootMatcher(matcher))
        : [],
    [matchers, rootMatchers],
  );
  const inFlightRef = React.useRef(false);
  const backgroundInFlightRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    let hasLoadedBackground = false;

    const load = async (showLoading: boolean) => {
      if (inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;

      if (showLoading) {
        setState(prev => ({
          ...prev,
          loading: true,
          backgroundLoading: secondaryMatchers.length > 0,
          error: undefined,
        }));
      }

      try {
        const targetClusters = await resolveTargetClusters(
          kubernetesApi,
          scopedClusterNames,
        );
        const primaryMatchers = rootMatchers.length > 0 ? rootMatchers : matchers;
        const response = await fetchClusterWideResourcesForMatchers(
          kubernetesApi,
          targetClusters,
          primaryMatchers,
        );
        if (cancelled) {
          return;
        }
        setState(prev => {
          const mergedClusters = showLoading
            ? response.results
            : response.results.reduce(
                (acc, cluster) =>
                  mergeClusterResources(acc, cluster.clusterName, cluster.resources),
                prev.clusters,
              );

          return {
            loading: false,
            backgroundLoading:
              secondaryMatchers.length > 0 &&
              (showLoading || prev.backgroundLoading || backgroundInFlightRef.current),
            error: undefined,
            warnings: mergeWarnings(
              showLoading ? [] : prev.warnings,
              response.warnings,
            ),
            clusters: mergedClusters,
          };
        });

        if (
          secondaryMatchers.length > 0 &&
          !hasLoadedBackground &&
          !backgroundInFlightRef.current &&
          !cancelled
        ) {
          backgroundInFlightRef.current = true;

          void (async () => {
            try {
              for (const cluster of targetClusters) {
                for (const matcher of secondaryMatchers) {
                  if (cancelled) {
                    return;
                  }

                  const matcherResponse = await fetchMatcherResources(
                    kubernetesApi,
                    cluster.name,
                    matcher,
                  );

                  if (cancelled) {
                    return;
                  }

                  if (
                    matcherResponse.resources.length > 0 ||
                    matcherResponse.warnings.length > 0
                  ) {
                    setState(prev => ({
                      ...prev,
                      warnings: mergeWarnings(prev.warnings, matcherResponse.warnings),
                      clusters: mergeClusterResources(
                        prev.clusters,
                        cluster.name,
                        matcherResponse.resources,
                      ),
                    }));
                  }
                }
              }
            } finally {
              hasLoadedBackground = true;
              backgroundInFlightRef.current = false;
              if (!cancelled) {
                setState(prev => ({
                  ...prev,
                  backgroundLoading: false,
                }));
              }
            }
          })();
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        setState(prev => ({
          ...prev,
          loading: false,
          backgroundLoading: false,
          error: error instanceof Error ? error.message : String(error),
        }));
      } finally {
        inFlightRef.current = false;
      }
    };

    void load(true);

    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) {
        return;
      }
      void load(false);
    }, refreshIntervalMs);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [
    kubernetesApi,
    matchersKey,
    scopedClustersKey,
    refreshIntervalMs,
    matchers,
    scopedClusterNames,
    rootMatchers,
    secondaryMatchers,
  ]);

  return state;
};

type TraceRowsProps = {
  nodes: CrossplaneNode[];
  depth: number;
  expandedRows: Record<string, boolean>;
  onToggle: (nodeId: string) => void;
};

const TraceRows = ({ nodes, depth, expandedRows, onToggle }: TraceRowsProps) => (
  <>
    {nodes.map(node => {
      const hasChildren = node.children.length > 0;
      const isExpanded = expandedRows[node.id] ?? false;
      const namespace = node.resource.metadata?.namespace;

      return (
        <React.Fragment key={node.id}>
          <TableRow>
            <TableCell>
              <Box
                display="flex"
                alignItems="center"
                style={{ paddingLeft: depth * 20 }}
              >
                {hasChildren ? (
                  <IconButton
                    size="small"
                    onClick={() => onToggle(node.id)}
                    aria-label={isExpanded ? 'collapse' : 'expand'}
                  >
                    {isExpanded ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </IconButton>
                ) : (
                  <Box width={32} />
                )}

                <Box>
                  <Typography variant="body2">{nodeLabel(node.resource)}</Typography>
                  {namespace ? (
                    <Typography variant="caption" color="textSecondary">
                      namespace: {namespace}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            </TableCell>
            <TableCell align="center">
              <Typography variant="body2" style={conditionValueStyle(node.synced)}>
                {node.synced}
              </Typography>
            </TableCell>
            <TableCell align="center">
              <Typography variant="body2" style={conditionValueStyle(node.ready)}>
                {node.ready}
              </Typography>
            </TableCell>
            <TableCell>
              <Typography variant="body2">{node.status}</Typography>
            </TableCell>
          </TableRow>
          {hasChildren && isExpanded ? (
            <TraceRows
              nodes={node.children}
              depth={depth + 1}
              expandedRows={expandedRows}
              onToggle={onToggle}
            />
          ) : null}
        </React.Fragment>
      );
    })}
  </>
);

export const isCrossplaneResourcesAvailable = (entity: Entity): boolean =>
  isKubernetesAvailable(entity);

export const EntityCrossplaneResourcesContent = () => {
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const defaultShowAll = React.useMemo(
    () => isKubernetesClusterEntity(entity),
    [entity],
  );
  const [showAll, setShowAll] = React.useState(defaultShowAll);

  React.useEffect(() => {
    setShowAll(defaultShowAll);
  }, [defaultShowAll]);

  const matchers = React.useMemo(
    () => parseMatchersFromConfig(configApi),
    [configApi],
  );

  const scopedClusterNames = React.useMemo(
    () => parseClusterScopeFromEntity(entity),
    [entity],
  );

  const { loading, backgroundLoading, error, warnings, clusters } =
    useClusterWideCrossplaneResources(
      matchers,
      REFRESH_INTERVAL_MS,
      scopedClusterNames,
    );

  const traces = React.useMemo(
    () => buildClusterTraces(clusters, entity, showAll),
    [clusters, entity, showAll],
  );

  const [expandedRows, setExpandedRows] = React.useState<Record<string, boolean>>(
    {},
  );

  const handleToggle = React.useCallback((nodeId: string) => {
    setExpandedRows(current => ({
      ...current,
      [nodeId]: !current[nodeId],
    }));
  }, []);

  const handleShowAllToggle = React.useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setShowAll(checked);
    },
    [],
  );

  const cardAction = (
    <FormControlLabel
      control={
        <Switch
          color="primary"
          checked={showAll}
          onChange={handleShowAllToggle}
          name="show-all-crossplane-resources"
        />
      }
      label="Show all"
    />
  );

  if (loading && clusters.length === 0) {
    return (
      <InfoCard title="Crossplane Resources" action={cardAction}>
        <Progress />
      </InfoCard>
    );
  }

  if (error && clusters.length === 0) {
    return (
      <InfoCard title="Crossplane Resources" action={cardAction}>
        <EmptyState
          missing="data"
          title="Unable to load Crossplane resources"
          description={error}
        />
      </InfoCard>
    );
  }

  if (!traces.length) {
    const clusterScopeDescription =
      showAll
        ? scopedClusterNames.length > 0
          ? `No Crossplane resources found in scoped cluster(s): ${scopedClusterNames.join(', ')}.`
          : `No Crossplane resources found.`
        : scopedClusterNames.length > 0
          ? `No associated Crossplane resources found in scoped cluster(s): ${scopedClusterNames.join(', ')}.`
          : `No associated Crossplane resources found.`;
    const warningsDescription =
      warnings.length > 0
        ? ` ${warnings.length} Crossplane request${warnings.length === 1 ? '' : 's'} failed (for example 403/404). Check backend logs or Kubernetes RBAC.`
        : '';

    return (
      <InfoCard title="Crossplane Resources" action={cardAction}>
        <EmptyState
          missing="data"
          title={clusterScopeDescription}
          description={`Use ${ANNOTATION_CROSSPLANE_CLUSTER}, ${ANNOTATION_CROSSPLANE_CLUSTERS}, or ${ANNOTATION_KUBERNETES_CLUSTER} to scope cluster queries, ${ANNOTATION_RESOURCE_NAME}, ${ANNOTATION_RESOURCE_NAMES}, or ${ANNOTATION_RESOURCE_NAME_PATTERN} to improve matching, and configure resource kinds under crossplane.customResources.${warningsDescription}`}
        />
      </InfoCard>
    );
  }

  return (
    <InfoCard title="Crossplane Resources" action={cardAction}>
      {backgroundLoading ? (
        <Box mb={1}>
          <Typography variant="caption" color="textSecondary">
            Updating subresource status in background...
          </Typography>
        </Box>
      ) : null}
      {traces.map(clusterTrace => (
        <Box key={clusterTrace.clusterName} mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Cluster: {clusterTrace.clusterName}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Showing {clusterTrace.totalResources}{' '}
            {showAll ? 'cluster' : 'associated'} resource
            {clusterTrace.totalResources === 1 ? '' : 's'} (
            {clusterTrace.fetchedResources} fetched cluster-wide).
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Resource</TableCell>
                  <TableCell align="center">Synced</TableCell>
                  <TableCell align="center">Ready</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TraceRows
                  nodes={clusterTrace.roots}
                  depth={0}
                  expandedRows={expandedRows}
                  onToggle={handleToggle}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </InfoCard>
  );
};
